import { Platform } from 'react-native';

/**
 * Returns true when the current runtime is Safari on macOS / iOS, OR
 * any browser on iOS (Chrome iOS, Firefox iOS — all of them use
 * WebKit under the hood and inherit the same iCloud Keychain quirks).
 *
 * Used by the auth screens (Login / Register / ResetPassword) to
 * decide whether to render password fields as `type="text"` with a
 * CSS-based mask (`-webkit-text-security: disc`) instead of the
 * native `type="password"`. Background:
 *
 * Safari aggressively hijacks any `type="password"` field — it spawns
 * the «Использовать надёжный пароль» Keychain popup on focus, which
 * either covers the field (macOS) or prevents the on-screen keyboard
 * from opening (iOS). `autoComplete="off"` is ignored by Safari on
 * password fields specifically (this is well-documented behaviour
 * dating back to Safari 9). The only reliable workaround we found is
 * to never let Safari see a password field in the first place — and
 * the CSS masking trick achieves that without changing the visual UX
 * (the user still sees dots instead of plaintext).
 *
 * The detection has to fire on both desktop Safari and *every* iOS
 * browser, because every iOS browser is required by Apple to use the
 * WebKit engine. Chrome iOS, Firefox iOS, Edge iOS — all inherit the
 * Keychain interception.
 *
 * Returns false on native (RN iOS / Android apps) because there the
 * native `secureTextEntry` prop works correctly — no workaround
 * needed.
 */
export function isSafariOnWeb(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';

  // Any browser on iOS uses WebKit and gets the same Keychain
  // popup, so detect iOS device first.
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  if (isIOSDevice) return true;

  // Desktop Safari: UA contains "Safari" but NOT any of the
  // WebKit-pretender markers (Chrome/Edg/Opera/Firefox all include
  // "Safari" in their UA on macOS for historical reasons).
  const isDesktopSafari =
    /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox/.test(ua);
  return isDesktopSafari;
}

/**
 * CORE-1289 — веб открыт на телефоне (Android / iOS), где у человека может
 * стоять наше приложение. Нужно экрану «почта подтверждена»: ссылка из письма
 * попадает в браузер, когда App Link / Universal Link не сработал (iOS без
 * associated domains, неподтверждённый assetlinks), и оттуда надо предложить
 * путь обратно в приложение. На нативе и десктопе — false.
 */
export function isMobileBrowser(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined') return false;
  return /Android|iPad|iPhone|iPod/.test(navigator.userAgent || '');
}
